import { useState } from 'react';
import { act } from '../../lib/actions';
import { api } from '../../lib/api';
import type { QaDefinition, QaQuestion, Scorecard } from '../Quality';
import { Button } from '../ui';
import { AddButton, Cell, ConfirmButton, Drawer, Field, inputClass, Pill, Row, SectionHeader, Table, useLoad } from './kit';

const CHANNEL: Record<Scorecard['channel'], string> = { all: 'Every channel', voice: 'Calls', chat: 'Chats (livechat, WhatsApp, Telegram)', email: 'Emails' };
const newId = () => Math.random().toString(36).slice(2, 10);
const blankQuestion = (): QaQuestion => ({ id: newId(), text: '', type: 'yes_no', points: 10, critical: false, allowNa: false });

/** A starting point most teams adapt. */
const STARTER: QaDefinition = {
  sections: [
    {
      id: newId(),
      title: 'Opening',
      questions: [
        { id: newId(), text: 'Greeted the customer and gave their name', type: 'yes_no', points: 10 },
        { id: newId(), text: "Verified the customer's identity", type: 'yes_no', points: 15, critical: true, allowNa: true },
      ],
    },
    {
      id: newId(),
      title: 'Handling',
      questions: [
        { id: newId(), text: 'Understood the problem', type: 'rating', points: 20 },
        { id: newId(), text: 'Showed empathy and stayed courteous', type: 'rating', points: 15 },
        { id: newId(), text: 'Gave a correct, complete answer', type: 'rating', points: 25, critical: true },
      ],
    },
    {
      id: newId(),
      title: 'Closing',
      questions: [
        { id: newId(), text: 'Checked if there was anything else', type: 'yes_no', points: 5 },
        { id: newId(), text: 'Chose the right outcome and wrote clear notes', type: 'yes_no', points: 10 },
      ],
    },
  ],
};

/**
 * Quality scorecards: the forms reviewers fill in. Changing one that has been
 * used saves a new version; old reviews keep the questions they were scored on.
 */
export function Scorecards() {
  const [cards, reload] = useLoad<Scorecard[]>('/qa/scorecards');
  const [editing, setEditing] = useState<Scorecard | 'new' | null>(null);
  return (
    <>
      <SectionHeader
        title="Scorecards"
        lead="The forms QA staff and supervisors fill in when they review a call, chat or email. Each question is worth points; a critical question failed fails the review whatever the score."
        action={<AddButton onClick={() => setEditing('new')}>New scorecard</AddButton>}
      />
      <Table head={['Scorecard', 'For', 'Questions', 'Pass mark', 'Reviews']} empty={cards?.length === 0 && 'No scorecards yet. Start from the example and adapt it.'}>
        {(cards ?? []).map((c) => (
          <Row key={c.id} onClick={() => setEditing(c)}>
            <Cell>
              <span className="font-medium">{c.name}</span> <span className="text-[12px] text-ink-3">v{c.version}</span>
            </Cell>
            <Cell>
              <Pill>{CHANNEL[c.channel]}</Pill>
            </Cell>
            <Cell className="tabular">{c.definition.sections.reduce((n, s) => n + s.questions.length, 0)}</Cell>
            <Cell className="tabular">{c.pass_mark}%</Cell>
            <Cell className="tabular">{c.reviews}</Cell>
          </Row>
        ))}
      </Table>
      {editing ? (
        <ScorecardDrawer
          card={editing === 'new' ? null : editing}
          onDone={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function ScorecardDrawer({ card, onDone }: { card: Scorecard | null; onDone: () => void }) {
  const [name, setName] = useState(card?.name ?? 'Customer service');
  const [channel, setChannel] = useState<Scorecard['channel']>(card?.channel ?? 'all');
  const [passMark, setPassMark] = useState(String(card?.pass_mark ?? 80));
  const [def, setDef] = useState<QaDefinition>(card?.definition ?? STARTER);
  const [busy, setBusy] = useState(false);
  const total = def.sections.reduce((n, s) => n + s.questions.reduce((m, q) => m + q.points, 0), 0);
  const valid = name.trim().length >= 2 && def.sections.every((s) => s.title.trim().length >= 2 && s.questions.length && s.questions.every((q) => q.text.trim().length >= 3 && q.points >= 1));

  const editSection = (i: number, patch: Partial<QaDefinition['sections'][number]>) => setDef((d) => ({ sections: d.sections.map((s, n) => (n === i ? { ...s, ...patch } : s)) }));
  const editQuestion = (i: number, j: number, patch: Partial<QaQuestion>) =>
    editSection(i, { questions: def.sections[i].questions.map((q, n) => (n === j ? { ...q, ...patch } : q)) });

  async function save() {
    setBusy(true);
    const body = { name, channel, passMark: Number(passMark), definition: def };
    const ok = await act(() => (card ? api('PATCH', `/qa/scorecards/${card.id}`, body) : api('POST', '/qa/scorecards', body)), card && card.reviews ? 'Saved as a new version' : 'Scorecard saved');
    setBusy(false);
    if (ok) onDone();
  }
  async function retire() {
    if (await act(() => api('POST', `/qa/scorecards/${card!.id}/retire`).then(() => true), 'Scorecard retired')) onDone();
  }

  return (
    <Drawer
      title={card ? `${card.name} (v${card.version})` : 'New scorecard'}
      open
      onClose={onDone}
      footer={
        <>
          <Button tone="primary" className="px-4 py-2" disabled={busy || !valid} onClick={() => void save()}>
            {card?.reviews ? 'Save as a new version' : 'Save'}
          </Button>
          <Button tone="ghost" className="px-3 py-2" onClick={onDone}>
            Cancel
          </Button>
          {card ? (
            <span className="ml-auto">
              <ConfirmButton confirmText="Retire it" onConfirm={() => void retire()}>
                Retire
              </ConfirmButton>
            </span>
          ) : null}
        </>
      }
    >
      {card?.reviews ? <p className="mb-4 text-[12px] text-ink-3">{card.reviews} reviews use this version. Saving changes makes version {card.version + 1}; those reviews keep these questions.</p> : null}
      <Field label="Name">{(id) => <input id={id} className={inputClass} value={name} onChange={(e) => setName(e.target.value)} />}</Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="For">
          {(id) => (
            <select id={id} className={inputClass} value={channel} onChange={(e) => setChannel(e.target.value as Scorecard['channel'])}>
              {Object.entries(CHANNEL).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Pass mark (%)">{(id) => <input id={id} type="number" min={0} max={100} className={`${inputClass} tabular`} value={passMark} onChange={(e) => setPassMark(e.target.value)} />}</Field>
      </div>
      <p className="mb-3 text-[12px] text-ink-3">{total} points in all. Yes earns all of a question's points and No none; a rating of 1 earns none and 5 all. N/A leaves the question out of the score.</p>
      {def.sections.map((s, i) => (
        <fieldset key={s.id} className="mb-4 rounded-lg border border-line p-3">
          <legend className="sr-only">Section {i + 1}</legend>
          <div className="mb-2 flex gap-2">
            <input aria-label={`Section ${i + 1} title`} className={`${inputClass} font-medium`} value={s.title} onChange={(e) => editSection(i, { title: e.target.value })} />
            <Button tone="ghost" className="px-2 text-xs" onClick={() => setDef((d) => ({ sections: d.sections.filter((_, n) => n !== i) }))} disabled={def.sections.length === 1}>
              Remove section
            </Button>
          </div>
          {s.questions.map((q, j) => (
            <div key={q.id} className="mb-2 rounded-md border border-line/70 p-2">
              <input aria-label={`Question ${j + 1} in ${s.title || `section ${i + 1}`}`} className={inputClass} value={q.text} placeholder="What the reviewer checks" onChange={(e) => editQuestion(i, j, { text: e.target.value })} />
              <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[12px]">
                <select aria-label="Answer" className="rounded-md border border-line bg-transparent px-1.5 py-1" value={q.type} onChange={(e) => editQuestion(i, j, { type: e.target.value as QaQuestion['type'] })}>
                  <option value="yes_no">Yes / No</option>
                  <option value="rating">Rating 1–5</option>
                </select>
                <label className="flex items-center gap-1">
                  Points
                  <input type="number" min={1} max={100} aria-label="Points" className="tabular w-14 rounded-md border border-line bg-transparent px-1.5 py-1" value={q.points} onChange={(e) => editQuestion(i, j, { points: Number(e.target.value) })} />
                </label>
                <label className="flex items-center gap-1">
                  <input type="checkbox" checked={Boolean(q.critical)} onChange={(e) => editQuestion(i, j, { critical: e.target.checked })} /> Critical
                </label>
                <label className="flex items-center gap-1">
                  <input type="checkbox" checked={Boolean(q.allowNa)} onChange={(e) => editQuestion(i, j, { allowNa: e.target.checked })} /> N/A allowed
                </label>
                <button type="button" className="ml-auto text-ink-3 hover:text-break" onClick={() => editSection(i, { questions: s.questions.filter((_, n) => n !== j) })} disabled={s.questions.length === 1}>
                  Remove
                </button>
              </div>
            </div>
          ))}
          <Button tone="quiet" className="mt-1 px-2.5 py-1 text-xs" onClick={() => editSection(i, { questions: [...s.questions, blankQuestion()] })}>
            Add question
          </Button>
        </fieldset>
      ))}
      <Button tone="quiet" className="px-3 py-1.5 text-sm" onClick={() => setDef((d) => ({ sections: [...d.sections, { id: newId(), title: '', questions: [blankQuestion()] }] }))}>
        Add section
      </Button>
    </Drawer>
  );
}
